// Plans of Record — lists all plans marked status='committed' across
// projects. Click a plan to open it in the Territory page (MapPage reads
// ?planId= on mount).
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from '../lib/toast';

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;
const fmtNum = (n: number) => Math.round(n).toLocaleString();
const fmtDate = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

type Plan = {
  id: string;
  name: string;
  status: string;
  project_id: string;
  project_name: string;
  updated_at: string;
  created_at: string;
  summary: any;
  clusters: any[];
};

export default function CommittedPlansPage() {
  const navigate = useNavigate();
  const [compareSel, setCompareSel] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setCompareSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const { data: plans = [], isLoading } = useQuery<Plan[]>({
    queryKey: ['committed-plans'],
    queryFn: () => api.get('/territory/committed'),
    // Always refetch when the user returns to this page so uncommits/deletes
    // from the Territory page show up immediately instead of stale rows.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const qc = useQueryClient();
  // Uncommit + delete. The backend refuses DELETE only on 'committed' plans,
  // and /uncommit 409s ('NotCommitted') on anything that isn't exactly
  // 'committed'. A 'complete' plan (already uncommitted / analyzed) is deleted
  // directly — calling /uncommit on it would 409 and block the delete.
  async function deletePlan(plan: Plan) {
    try {
      if (plan.status === 'committed') {
        await api.post(`/territory/plans/${plan.id}/uncommit`, {});
      }
      await api.del(`/territory/plans/${plan.id}`);
      return true;
    } catch (err: any) {
      toast.error(`Delete failed for ${plan.name}: ${err?.message ?? err}`);
      return false;
    }
  }
  const deleteMut = useMutation({
    mutationFn: (plan: Plan) => deletePlan(plan),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
  });
  const deleteAllMut = useMutation({
    mutationFn: async () => {
      let n = 0;
      for (const pl of plans) { if (await deletePlan(pl)) n += 1; }
      return n;
    },
    onSuccess: (n: number) => {
      toast.success(`Deleted ${n} plan${n === 1 ? '' : 's'} of record`);
      setCompareSel(new Set());
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
  });

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Plans of Record</h1>
          <div className="page__subtitle">Committed territory plans, mapped to BEAD awards</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {compareSel.size >= 1 && (
            <button
              onClick={() => {
                const ids = Array.from(compareSel).join(',');
                window.open(`/reports?planIds=${ids}`, '_blank');
              }}
              style={{ padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Compare in report ({compareSel.size})
            </button>
          )}
          {plans.length > 0 && (
            <button
              disabled={deleteAllMut.isPending}
              onClick={() => {
                if (window.confirm(`Delete ALL ${plans.length} committed plan${plans.length === 1 ? '' : 's'} of record?\n\nEach plan will be uncommitted and then deleted. This cannot be undone.`)) {
                  deleteAllMut.mutate();
                }
              }}
              style={{ padding: '8px 16px', background: '#fff', color: '#b91c1c', border: '1px solid #b91c1c', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              title="Uncommit and delete every plan in this list."
            >
              Delete all ({plans.length})
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="card"><div className="card__body">Loading…</div></div>
      )}

      {!isLoading && plans.length === 0 && (
        <div className="card">
          <div className="card__body" style={{ color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 14, marginBottom: 6 }}>No plans committed yet.</div>
            <div style={{ fontSize: 12 }}>
              Go to <b>Territory</b>, analyze a scenario, and click <b>✓ commit</b> next to its name in the Scenarios panel. Committed plans land here as your plan-of-record library.
            </div>
          </div>
        </div>
      )}

      {!isLoading && plans.map((p) => {
        const s = p.summary ?? {};
        const capex = Number(s.total_capex ?? (Number(s.total_cluster_capex ?? 0) + Number(s.total_backbone_capex ?? 0)));
        const mrr = Number(s.total_mrr ?? 0);
        const passings = Number(s.total_passings ?? 0);
        const beadIn = Number(s.bead_in_territory ?? 0);
        const beadUncov = Number(s.bead_uncovered ?? 0);
        const beadCov = beadIn - beadUncov;
        const grant = Number(s.max_grant_available ?? 0);
        const grantFederal = Number(s.grant_federal ?? 0);
        const grantMatch = Number(s.grant_match ?? 0);
        const privateCap = Math.max(0, capex - grant);
        const breakdown: any[] = Array.isArray(s.grant_breakdown_by_project) ? s.grant_breakdown_by_project : [];

        return (
          <div key={p.id} className="card" style={{ marginBottom: 12 }}>
            <div className="card__header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={compareSel.has(p.id)}
                onChange={() => toggle(p.id)}
                title="Select for side-by-side comparison"
                style={{ cursor: 'pointer', accentColor: '#059669', width: 16, height: 16 }}
              />
              <span style={{ padding: '2px 6px', background: p.status === 'committed' ? '#059669' : '#6b7280', color: '#fff', borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: '.04em' }}>{p.status === 'committed' ? 'COMMITTED' : 'COMPLETE'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Project: {p.project_name} · Committed {fmtDate(p.updated_at)}</div>
              </div>
              <button
                onClick={() => navigate(`/builder?planId=${p.id}`)}
                style={{ padding: '6px 12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Open in Territory
              </button>
              <button
                disabled={deleteMut.isPending}
                onClick={() => {
                  const pass = Number(p.summary?.total_passings ?? 0);
                  const isCommitted = p.status === 'committed';
                  const msg = `Delete ${isCommitted ? 'committed ' : ''}plan "${p.name}" (${pass.toLocaleString()} passings)?\n\n${isCommitted ? 'It will be uncommitted, then deleted.' : 'This plan of record will be deleted.'} This cannot be undone.`;
                  if (window.confirm(msg)) deleteMut.mutate(p);
                }}
                style={{ padding: '6px 12px', background: '#fff', color: '#b91c1c', border: '1px solid #b91c1c', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                title="Uncommit and delete this plan"
              >
                Delete
              </button>
            </div>

            <div className="card__body">
              {/* Hero numbers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
                <Metric label="Total CAPEX" value={fmtUSD(capex)} big />
                <Metric label="MRR" value={`${fmtUSD(mrr)}/mo`} />
                <Metric label="Passings" value={fmtNum(passings)} />
                <Metric label="BEAD in / covered" value={`${fmtNum(beadIn)} / ${fmtNum(beadCov)}`} tone={beadUncov === 0 ? 'good' : 'warn'} />
                <Metric label="Grant (federal+match)" value={fmtUSD(grant)} tone="good" />
                <Metric label="Private capital" value={fmtUSD(privateCap)} />
              </div>

              {/* Grant breakdown by project */}
              {breakdown.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#111827', marginBottom: 6 }}>BEAD award coverage</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={thStyle}>Project</th>
                        <th style={thStyleR}>BSLs in territory</th>
                        <th style={thStyleR}>Awarded BSLs</th>
                        <th style={thStyleR}>Ratio</th>
                        <th style={thStyleR}>Grant</th>
                        <th style={thStyleR}>Federal</th>
                        <th style={thStyleR}>Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((b: any) => (
                        <tr key={b.project_code} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}><b>{b.project_code}</b></td>
                          <td style={tdStyleR}>{fmtNum(Number(b.bsls_in_territory ?? 0))}</td>
                          <td style={tdStyleR}>{fmtNum(Number(b.awarded_bsl_count ?? 0))}</td>
                          <td style={tdStyleR}>{Number(b.ratio ?? 0).toFixed(1)}%</td>
                          <td style={tdStyleR}>{fmtUSD(Number(b.grant_for_this ?? 0))}</td>
                          <td style={tdStyleR}>{fmtUSD(Number(b.federal ?? 0))}</td>
                          <td style={tdStyleR}>{fmtUSD(Number(b.match ?? 0))}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #111827', fontWeight: 700 }}>
                        <td style={tdStyle}>Total</td>
                        <td style={tdStyleR}>{fmtNum(breakdown.reduce((a: number, b: any) => a + Number(b.bsls_in_territory ?? 0), 0))}</td>
                        <td style={tdStyleR}>—</td>
                        <td style={tdStyleR}>—</td>
                        <td style={tdStyleR}>{fmtUSD(grant)}</td>
                        <td style={tdStyleR}>{fmtUSD(grantFederal)}</td>
                        <td style={tdStyleR}>{fmtUSD(grantMatch)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

function Metric({ label, value, big, tone }: { label: string; value: string; big?: boolean; tone?: 'good' | 'warn' }) {
  const color = tone === 'good' ? '#047857' : tone === 'warn' ? '#b45309' : '#111827';
  return (
    <div style={{ padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: '#fff' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: big ? 20 : 15, fontWeight: 800, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

const thStyle: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#111827', borderBottom: '1px solid var(--border)' };
const thStyleR: React.CSSProperties = { ...thStyle, textAlign: 'right' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', textAlign: 'left' };
const tdStyleR: React.CSSProperties = { ...tdStyle, textAlign: 'right' };
